import { useEffect, useState } from 'react';
import AccountSwitchHeader from '@/components/feature/AccountSwitchHeader';
import PeriodPicker, { monthRange } from '@/components/feature/PeriodPicker';
import type { Period } from '@/components/feature/PeriodPicker';
import { getCommissionDetails } from '@/api';
import type { AccountDetails } from '@/api';

export default function CommissionDetailsPage() {
  const [period, setPeriod] = useState<Period>(() => monthRange(0));
  const [commissionDetails, setCommissionDetails] = useState<AccountDetails | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    getCommissionDetails(1, 50)
      .then((res) => {
        if (mounted) setCommissionDetails(res);
      })
      .catch((err) => {
        if (mounted) setError(err instanceof Error ? err.message : '加载失败');
      });
    return () => {
      mounted = false;
    };
  }, []);

  //按账期过滤记录并统计（佣金记录均为收入）
  const periodRecords = commissionDetails
    ? commissionDetails.records.filter((r) => {
        const day = r.date.replace(/\//g, '-').slice(0, 10);
        return day >= period.start && day <= period.end;
      })
    : [];
  const periodIncome = periodRecords.reduce((s, r) => s + r.amount, 0);

  //加载中/失败
  if (!commissionDetails) {
    return (
      <div className="mobile-container pb-10">
        <AccountSwitchHeader current="commission" />
        <div className="flex flex-col items-center justify-center py-24">
          {error ? (
            <>
              <i className="ri-wifi-off-line text-foreground-300 text-4xl mb-3" />
              <p className="text-foreground-400 text-sm">{error}</p>
            </>
          ) : (
            <>
              <i className="ri-loader-4-line text-primary-400 text-3xl animate-spin mb-3" />
              <p className="text-foreground-400 text-sm">加载中...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-container pb-10">
      {/* Header */}
      <AccountSwitchHeader current="commission" />

      <div className="px-4 space-y-4 pt-4">
        {/* Balance Card — blue/purple gradient */}
        <div className="relative rounded-2xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400 via-indigo-500 to-violet-500"></div>
          {/* Decorative circles */}
          <div className="absolute -top-8 -right-8 w-36 h-36 rounded-full bg-white/10"></div>
          <div className="absolute -bottom-6 right-10 w-20 h-20 rounded-full bg-white/8"></div>
          <div className="absolute top-6 right-20 w-10 h-10 rounded-full bg-white/12"></div>

          <div className="relative px-5 py-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/80 text-xs font-medium tracking-wide mb-1">累计推广佣金</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-white text-xl font-medium">¥</span>
                  <span className="text-white text-4xl font-bold tracking-tight">
                    {Number(commissionDetails.balance).toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20">
                <i className="ri-share-forward-fill text-white text-xl"></i>
              </div>
            </div>

            {/* Stats row */}
            <div className="mt-4 flex items-center gap-3">
              <div className="flex-1 bg-white/15 rounded-xl px-3 py-2">
                <p className="text-white/70 text-xs mb-0.5">本期收入</p>
                <p className="text-white font-semibold text-sm">+¥{periodIncome.toFixed(2)}</p>
              </div>
              <div className="flex-1 bg-white/15 rounded-xl px-3 py-2">
                <p className="text-white/70 text-xs mb-0.5">本期支出</p>
                <p className="text-white font-semibold text-sm">¥0.00</p>
              </div>
              <div className="flex-1 bg-white/15 rounded-xl px-3 py-2">
                <p className="text-white/70 text-xs mb-0.5">笔数</p>
                <p className="text-white font-semibold text-sm">{periodRecords.length} 笔</p>
              </div>
            </div>
          </div>
        </div>

        {/* Period Selector */}
        <PeriodPicker period={period} onChange={setPeriod} />

        {/* Section Title */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 rounded-full bg-indigo-500"></div>
            <span className="text-sm font-semibold text-foreground-800">佣金记录</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span>
              收入：
              <span className="text-indigo-600 font-semibold">+¥{periodIncome.toFixed(2)}</span>
            </span>
            <span>
              支出：
              <span className="text-foreground-600 font-medium">¥0.00</span>
            </span>
          </div>
        </div>

        {/* Commission Cards — horizontal layout matching screenshot */}
        <div className="space-y-3">
          {periodRecords.map((record) => (
            <div
              key={record.id}
              className="bg-white rounded-2xl px-4 py-4 border border-background-100 hover:border-background-200 transition-colors"
            >
              {/* Top row: type + order + amount */}
              <div className="flex items-start justify-between mb-2.5">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-primary-50 text-primary-500 text-xs font-semibold">
                      {record.type}
                    </span>
                    <span className="text-foreground-400 text-xs font-mono tracking-tight truncate">
                      {record.orderNumber}
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0 ml-3">
                  <p className="text-indigo-600 text-lg font-bold">+¥{Number(record.amount).toFixed(2)}</p>
                </div>
              </div>

              {/* Trade amount */}
              <div className="flex items-center gap-1 text-xs mb-2.5">
                <span className="text-foreground-400">订单成交额：</span>
                <span className="text-foreground-700 font-semibold">
                  ¥{Number(record.tradeAmount).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                </span>
              </div>

              {/* Seller / Buyer row */}
              <div className="flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-primary-500 text-white text-[10px] font-semibold">
                    卖家
                  </span>
                  <span className="text-foreground-700 font-medium">{record.sellerName}</span>
                  <span className="text-foreground-400 font-mono">{record.sellerPhone}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs mt-1.5">
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-accent-500 text-white text-[10px] font-semibold">
                    买家
                  </span>
                  <span className="text-foreground-700 font-medium">{record.buyerName}</span>
                  <span className="text-foreground-400 font-mono">{record.buyerPhone}</span>
                </div>
              </div>

              {/* Date */}
              <p className="text-foreground-400 text-xs mt-2.5">{record.date}</p>
            </div>
          ))}
        </div>

        {/* Bottom */}
        <div className="flex items-center justify-center gap-2 py-3">
          <div className="h-px w-12 bg-background-200"></div>
          <span className="text-foreground-400 text-xs">已显示全部 {commissionDetails.count} 笔记录</span>
          <div className="h-px w-12 bg-background-200"></div>
        </div>
      </div>
    </div>
  );
}